import { Box, Center, Flex, Grid, Image, Spinner, Text, useDisclosure, useToast } from '@chakra-ui/react'
import { Button as UbiButton } from '@ubirider/pick-component-library'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { Card } from './Card'
import { DeleteCardModal } from '../../../../modals/DeleteCardModal'
import { deleteCard } from '../../../../api/cards'

import trainCard from '../../../../assets/train_card.svg'
import checkWhiteIcon from '../../../../assets/check_circle_white.svg'
import closeWhiteIcon from '../../../../assets/cross_white_24.svg'
import { SwitchIOModal } from '../../../../modals/SwitchIOModal/SwitchIOModal'
import whitePlus from '../../../../assets/plus_white.svg'
import secondaryPlus from '../../../../assets/plus_secondary.svg'

export const CardsMenu = ({ cards, loading }) => {
  const { t } = useTranslation()
  const toast = useToast()
  const toastIdRef = useRef()

  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isOpenSwitchIO, onOpen: onOpenSwitchIO, onClose: onCloseSwitchIO } = useDisclosure()

  const [showAllCards, setShowAllCards] = useState(false)
  const [removeLoading, setRemoveLoading] = useState(false)
  const [removeCardToken, setRemoveCardToken] = useState(null)
  const [switchTrigger, setSwitchTrigger] = useState(false)

  const [hasAnimated, setHasAnimated] = useState(false)

  const MFlex = motion.create(Flex)
  const MGrid = motion.create(Grid)
  const MText = motion.create(Text)

  const variants = {
    container: {
      hidden: { opacity: 0 },
      visible: { opacity: 1, transition: { when: 'beforeChildren', staggerChildren: 0.08, delayChildren: 0.05 } }
    },
    header: {
      hidden: { opacity: 0, y: 8 },
      visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 360, damping: 28 } }
    },
    section: {
      hidden: { opacity: 0, y: 10 },
      visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 360, damping: 26 } }
    },
    card: {
      hidden: { opacity: 0, y: 10, scale: 0.98 },
      visible: { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', stiffness: 360, damping: 26 } }
    },
    buttonLate: {
      hidden: { opacity: 0, y: 8 },
      visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 360, damping: 26, delay: 0.25 } }
    }
  }

  function close() {
    if (toastIdRef.current) toast.close(toastIdRef.current)
  }

  const onConfirm = async () => {
    setRemoveLoading(true)
    try {
      await deleteCard(removeCardToken)
      toastIdRef.current = toast({
        position: 'top',
        duration: 5000,
        isClosable: true,
        render: () => (
          <Box alignItems='center' color='white' bg='#01AE70' border='1px solid #067F5B' boxShadow='box-shadow: 0px 1px 10px 1px rgba(0, 0, 0, 0.05)' borderRadius='4px'>
            <Flex justifyContent='space-between' gap='16px' padding='16px'>
              <Image src={checkWhiteIcon} />
              <Text fontSize='14px' fontWeight='600'>
                {t('home.payment_card_deleted')}
              </Text>
              <Image cursor='pointer' onClick={close} src={closeWhiteIcon} />
            </Flex>
          </Box>
        )
      })
    } catch (error) {
      toast({ description: 'Error.', status: 'error', duration: 3000, isClosable: true })
    }
    onClose()
    setRemoveLoading(false)
    setRemoveCardToken(null)
  }

  useEffect(() => {
    if (switchTrigger) onOpenSwitchIO()
  }, [switchTrigger])

  return (
    <MFlex
      bg={{ sm: '', md: 'neutral.100', lg: 'neutral.100', xl: 'neutral.100' }}
      rounded='8px'
      padding={{ sm: '0', md: '24px', lg: '24px', xl: '24px' }}
      direction='column'
      width='100%'
      gap={{ sm: '24px', md: '16px', lg: '16px', xl: '16px' }}
      initial={hasAnimated ? false : 'hidden'}
      animate='visible'
      variants={variants.container}
      onAnimationComplete={() => setHasAnimated(true)}
    >
      <MFlex gap={{ sm: '8px', md: '16px', lg: '16px', xl: '16px' }} variants={variants.header}>
        <MText fontWeight='700' fontSize='24px' variants={variants.header}>
          {t('demo.my_payment_methods')}
        </MText>
      </MFlex>

      {loading ? (
        <Center minH='240px'>
          <Spinner thickness='6px' color='neutral.500' size='xl' />
        </Center>
      ) : cards != null && cards.length > 0 ? (
        <MFlex direction='column' gap='16px' variants={variants.section}>
          <Flex>
            <motion.div variants={variants.buttonLate} animate={hasAnimated ? 'visible' : 'hidden'}>
              <UbiButton
                onClick={() => setSwitchTrigger(true)}
                style='ghost'
                type='primary'
                leftIconProps={{ src: secondaryPlus }}
                label={t('home.add_payment_method')}
                css={{ padding: '16px 4px' }}
              />
            </motion.div>
          </Flex>

          {/* Desktop / tablet */}
          <MFlex direction='column' overflow='hidden' align='center' mr='-24px' ml='-24px' mb='-24px' display={{ sm: 'none', md: 'flex' }} variants={variants.section}>
            <MGrid
              variants={variants.container}
              templateColumns={{
                sm: `repeat(${cards.length}, 1fr)`,
                md: 'repeat(2, 1fr)',
                lg: 'repeat(2, 1fr)',
                xl: 'repeat(2, 1fr)',
                xxl: 'repeat(2, 1fr)'
              }}
              gap='24px'
              overflowX='scroll'
              w='100%'
              p='24px'
              sx={{ '::-webkit-scrollbar': { display: 'none' } }}
            >
              {(!showAllCards ? cards.slice(0, 2) : cards).map((card, i) => (
                <motion.div key={card?.token ?? i} variants={variants.card} animate={hasAnimated ? 'visible' : 'hidden'} layout>
                  <Card
                    card={card}
                    onRemove={() => {
                      setRemoveCardToken(card.token)
                      onOpen()
                    }}
                  />
                </motion.div>
              ))}
            </MGrid>
          </MFlex>

          {/* Mobile */}
          <MFlex direction='column' overflow='hidden' align='center' justify='space-between' mr='-24px' ml='-24px' display={{ sm: 'flex', md: 'none' }} variants={variants.section}>
            <MGrid
              variants={variants.container}
              templateColumns={{
                sm: `repeat(${cards.length}, 1fr)`,
                md: 'repeat(2, 1fr)',
                lg: 'repeat(2, 1fr)',
                xl: 'repeat(2, 1fr)',
                xxl: 'repeat(3, 1fr)'
              }}
              gap='24px'
              overflowX='scroll'
              w='100%'
              pb='24px'
              pl='24px'
              sx={{ '::-webkit-scrollbar': { display: 'none' } }}
            >
              {cards.map((card, i) => (
                <motion.div key={card?.token ?? i} variants={variants.card} animate={hasAnimated ? 'visible' : 'hidden'} layout>
                  <Card
                    card={card}
                    onRemove={() => {
                      setRemoveCardToken(card.token)
                      onOpen()
                    }}
                  />
                </motion.div>
              ))}
            </MGrid>
          </MFlex>

          {cards.length > 2 && (
            <Flex justify='flex-end' display={{ sm: 'none', md: 'flex' }}>
              <motion.div variants={variants.buttonLate} animate={hasAnimated ? 'visible' : 'hidden'}>
                <UbiButton
                  onClick={() => setShowAllCards(!showAllCards)}
                  style='ghost'
                  type='primary'
                  label={showAllCards ? t('home.see_less_methods') : t('home.see_all_methods')}
                  css={{ paddingRight: 16 }}
                />
              </motion.div>
            </Flex>
          )}
        </MFlex>
      ) : (
        <MFlex
          direction='column'
          my={{ sm: '', md: '48px', lg: '48px', xl: '48px' }}
          mx={{ sm: '', md: '96px', lg: '96px', xl: '96px' }}
          justifyContent='center'
          alignItems='center'
          textAlign='center'
          gap='24px'
          variants={variants.section}
        >
          <motion.div variants={variants.header} animate={hasAnimated ? 'visible' : 'hidden'}>
            <Image src={trainCard} w='260px' />
          </motion.div>

          <MText fontSize={{ sm: '18px', md: '18px', lg: '14px', xl: '14px' }} color='neutral.800' textAlign='center' variants={variants.header} animate={hasAnimated ? 'visible' : 'hidden'}>
            {t('demo.my_contactless_bank_cards_empty_state')}
          </MText>

          <motion.div variants={variants.buttonLate} animate={hasAnimated ? 'visible' : 'hidden'}>
            <Center display={{ sm: 'flex', md: 'flex', lg: 'none', xl: 'none' }}>
              <UbiButton
                onClick={() => setSwitchTrigger(true)}
                style='solid'
                type='primary'
                leftIconProps={{ src: whitePlus }}
                label={t('home.add_payment_method')}
              />
            </Center>
          </motion.div>

          <motion.div variants={variants.buttonLate} animate={hasAnimated ? 'visible' : 'hidden'}>
            <Center display={{ sm: 'none', md: 'none', lg: 'flex', xl: 'flex' }}>
              <UbiButton
                onClick={() => setSwitchTrigger(true)}
                style='solid'
                type='primary'
                leftIconProps={{ src: whitePlus }}
                label={t('home.add_payment_method')}
              />
            </Center>
          </motion.div>
        </MFlex>
      )}

      <DeleteCardModal isOpen={isOpen} onClose={onClose} onConfirm={onConfirm} loading={removeLoading} />
      <SwitchIOModal onOpen={onOpenSwitchIO} onClose={onCloseSwitchIO} isOpen={isOpenSwitchIO} switch={switchTrigger} setSwitch={setSwitchTrigger} />
    </MFlex>
  )
}